import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Empty, Tag, Space, Typography, Badge, Spin, Slider, Button } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import axios from 'axios';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import Map, { Layer, Marker, Source } from 'react-map-gl';
import type { MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import dayjs from 'dayjs';
import { PatternLink } from '../PatternLink';

const { Text } = Typography;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';

interface UsageHistoryItem {
    date: string;
    measurementCount: number;
    measuredPointIds: number[];
}

interface PatternStats {
    totalMeasurements: number;
    avgMeasurementsPerDay: number;
}

interface PatternData {
    id: number;
    name: string;
    patternType: string;
    coordSystem: string;
    numPoints: number;
    area?: { id: number; name: string };
    areaType?: { id: number; name: string };
    usageCount: number;
    usageHistory: UsageHistoryItem[];
    stats?: PatternStats;
}

interface PatternPoint {
    id: number;
    pointIndex: number;
    xLocal?: number;
    yLocal?: number;
    gpsLat?: number;
    gpsLon?: number;
    label?: string;
}

interface CommonSectionProps {
    section: any;
    data: any[];
    areas: any[];
    areaTypes: any[];
    metrics: any[];
    previewAreaId: number;
    onPatternClick?: (patternId: number, areaId: number) => void;
    onPatternPointClick?: (patternPointId: number, patternId: number, areaId: number) => void;
}

export const ReportPatternList: React.FC<CommonSectionProps> = ({
    section,
    data,
    areas,
    areaTypes,
    previewAreaId,
    onPatternClick,
    onPatternPointClick,
}) => {
    const [selectedPatternId, setSelectedPatternId] = useState<number | null>(null);
    const [patternPoints, setPatternPoints] = useState<PatternPoint[]>([]);
    const [loadingPoints, setLoadingPoints] = useState(false);
    const [templateUrl, setTemplateUrl] = useState<string | null>(null);
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [imageBounds, setImageBounds] = useState({ left: 0, top: 0, width: 0, height: 0 });
    const [areaGeometry, setAreaGeometry] = useState<any>(null);
    
    // Timeline state
    const [selectedDateIndex, setSelectedDateIndex] = useState<number>(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const playIntervalRef = useRef<NodeJS.Timeout | null>(null);
    
    const previewRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const mapRef = useRef<MapRef>(null);
    const templateObjectUrl = useRef<string | null>(null);

    const patterns: PatternData[] = data || [];
    const selectedPattern = patterns.find(p => p.id === selectedPatternId);
    const selectedArea = areas.find(a => a.id === previewAreaId);
    const selectedAreaType = selectedArea ? areaTypes.find(at => at.id === selectedArea.areaType?.id) : null;
    const isTemplateMode = selectedPattern?.coordSystem === 'local';
    
    // Get current usage date and measured point IDs
    const usageHistory = selectedPattern?.usageHistory || [];
    const currentUsage = usageHistory[selectedDateIndex] || null;
    const measuredPointIds = currentUsage?.measuredPointIds || [];
    const usageWindowLabel = useMemo(() => {
        const dates = patterns
            .flatMap((pattern) => pattern.usageHistory || [])
            .map((usage) => usage.date)
            .filter(Boolean)
            .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());

        if (dates.length === 0) {
            return selectedArea?.name || 'Pattern list';
        }

        return `${selectedArea?.name || 'Pattern list'} · ${dayjs(dates[0]).format('YYYY-MM-DD')} → ${dayjs(dates[dates.length - 1]).format('YYYY-MM-DD')}`;
    }, [patterns, selectedArea]);
    const selectedPatternWindowLabel = useMemo(() => {
        if (!selectedPattern?.usageHistory?.length) return null;
        const dates = selectedPattern.usageHistory
            .map((usage) => usage.date)
            .filter(Boolean)
            .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
        if (dates.length === 0) return null;
        return `${dayjs(dates[0]).format('MMM D, YYYY')} → ${dayjs(dates[dates.length - 1]).format('MMM D, YYYY')}`;
    }, [selectedPattern]);
    const measuredPointLabels = useMemo(() => {
        return measuredPointIds
            .map((id) => patternPoints.find((point) => point.id === id)?.pointIndex)
            .filter((value): value is number => value !== undefined && value !== null);
    }, [measuredPointIds, patternPoints]);

    // Auto-select first pattern
    useEffect(() => {
        if (patterns.length > 0 && !selectedPatternId) {
            setSelectedPatternId(patterns[0].id);
        }
    }, [patterns, selectedPatternId]);

    // Load pattern points when pattern selected
    useEffect(() => {
        const loadPoints = async () => {
            if (!selectedPatternId) {
                setPatternPoints([]);
                return;
            }
            setLoadingPoints(true);
            try {
                const res = await axios.get(`${ENDPOINTS.PATTERNS}/${selectedPatternId}/points`, {
                    withCredentials: true,
                });
                const pointsData = res.data?.data || res.data || [];
                setPatternPoints(pointsData.sort((a: any, b: any) => (a.pointIndex || 0) - (b.pointIndex || 0)));
            } catch (err) {
                console.error('Failed to load pattern points', err);
                setPatternPoints([]);
            } finally {
                setLoadingPoints(false);
            }
        };
        loadPoints();
    }, [selectedPatternId]);

    // Load template image for template-based patterns
    useEffect(() => {
        const loadTemplate = async () => {
            if (!selectedAreaType?.id || !isTemplateMode) {
                setTemplateUrl(null);
                return;
            }
            setLoadingTemplate(true);
            try {
                const res = await axios.get(
                    `${ENDPOINTS.AREA_TYPES_V2}/${selectedAreaType.id}/template-image/download?t=${Date.now()}`,
                    { withCredentials: true, responseType: 'blob' }
                );
                if (templateObjectUrl.current) URL.revokeObjectURL(templateObjectUrl.current);
                const url = URL.createObjectURL(res.data as Blob);
                templateObjectUrl.current = url;
                setTemplateUrl(url);
            } catch (err) {
                console.warn('Failed to load template image', err);
                setTemplateUrl(null);
            } finally {
                setLoadingTemplate(false);
            }
        };
        loadTemplate();

        return () => {
            if (templateObjectUrl.current) {
                URL.revokeObjectURL(templateObjectUrl.current);
                templateObjectUrl.current = null;
            }
        };
    }, [selectedAreaType?.id, isTemplateMode]);

    // Load area geometry for GPS-based patterns
    useEffect(() => {
        const loadAreaGeometry = async () => {
            if (!previewAreaId || isTemplateMode) {
                setAreaGeometry(null);
                return;
            }
            try {
                const res = await axios.get(ENDPOINTS.AREA_DETAIL(previewAreaId), { withCredentials: true });
                const payload = res.data?.data || res.data;
                setAreaGeometry(payload?.geom || null);
            } catch (err) {
                console.warn('Failed to load area geometry', err);
                setAreaGeometry(null);
            }
        };
        loadAreaGeometry();
    }, [previewAreaId, isTemplateMode]);

    // Calculate image bounds for template preview
    useEffect(() => {
        if (!templateUrl || !previewRef.current) return;

        const img = new Image();
        imageRef.current = img;

        const calculateBounds = () => {
            const container = previewRef.current;
            const image = imageRef.current;
            if (!container || !image || !image.complete) return;

            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;
            const imageAspect = image.naturalWidth / image.naturalHeight;
            const containerAspect = containerWidth / containerHeight;

            let displayWidth, displayHeight, offsetX, offsetY;

            if (containerAspect > imageAspect) {
                displayHeight = containerHeight;
                displayWidth = displayHeight * imageAspect;
                offsetX = (containerWidth - displayWidth) / 2;
                offsetY = 0;
            } else {
                displayWidth = containerWidth;
                displayHeight = displayWidth / imageAspect;
                offsetX = 0;
                offsetY = (containerHeight - displayHeight) / 2;
            }

            setImageBounds({
                left: offsetX,
                top: offsetY,
                width: displayWidth,
                height: displayHeight,
            });
        };

        img.onload = () => {
            setTimeout(calculateBounds, 100);
        };
        img.src = templateUrl;

        const resizeObserver = new ResizeObserver(() => {
            calculateBounds();
        });

        if (previewRef.current) {
            resizeObserver.observe(previewRef.current);
        }

        return () => {
            resizeObserver.disconnect();
            imageRef.current = null;
        };
    }, [templateUrl]);

    // Reset timeline when pattern changes
    useEffect(() => {
        setSelectedDateIndex(0);
        setIsPlaying(false);
        if (playIntervalRef.current) {
            clearInterval(playIntervalRef.current);
            playIntervalRef.current = null;
        }
    }, [selectedPatternId]);

    // Handle play/pause animation
    useEffect(() => {
        if (isPlaying && usageHistory.length > 0) {
            playIntervalRef.current = setInterval(() => {
                setSelectedDateIndex(prev => {
                    const next = prev + 1;
                    if (next >= usageHistory.length) {
                        setIsPlaying(false);
                        return 0;
                    }
                    return next;
                });
            }, 1000);
        } else if (playIntervalRef.current) {
            clearInterval(playIntervalRef.current);
            playIntervalRef.current = null;
        }

        return () => {
            if (playIntervalRef.current) {
                clearInterval(playIntervalRef.current);
                playIntervalRef.current = null;
            }
        };
    }, [isPlaying, usageHistory.length]);

    const renderTemplatePreview = () => {
        if (loadingTemplate) {
            return (
                <div className="report-pattern-stage report-pattern-stage--placeholder">
                    <Spin tip="Loading template..." />
                </div>
            );
        }

        if (!templateUrl) {
            return (
                <div className="report-pattern-stage report-pattern-stage--placeholder">
                    <Empty description="No template image available" />
                </div>
            );
        }

        return (
            <div
                ref={previewRef}
                className="report-pattern-stage"
                style={{
                    backgroundImage: `url(${templateUrl})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundColor: '#fafafa',
                    position: 'relative',
                    width: '100%',
                    height: 400,
                }}
            >
                {imageBounds.width > 0 &&
                    patternPoints
                        .filter(p => p.xLocal != null && p.yLocal != null)
                        .map(p => {
                            const isMeasured = measuredPointIds.includes(p.id);
                            return (
                                <div
                                    key={p.pointIndex}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onPatternPointClick && selectedPattern) {
                                            onPatternPointClick(p.id, selectedPattern.id, previewAreaId);
                                        }
                                    }}
                                    title={`Click to view reports for Point ${p.pointIndex}`}
                                    style={{
                                        position: 'absolute',
                                        left: `${imageBounds.left + p.xLocal! * imageBounds.width}px`,
                                        top: `${imageBounds.top + (1 - p.yLocal!) * imageBounds.height}px`,
                                        transform: 'translate(-50%, -50%)',
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        background: isMeasured ? '#fa8c16' : '#d9d9d9',
                                        border: '2px solid white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 10,
                                        fontWeight: 'bold',
                                        color: 'white',
                                        zIndex: 10,
                                        opacity: isMeasured ? 1 : 0.5,
                                        cursor: onPatternPointClick ? 'pointer' : 'default',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (onPatternPointClick) {
                                            e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.2)';
                                            e.currentTarget.style.boxShadow = '0 0 8px rgba(250, 140, 22, 0.6)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                >
                                    {p.pointIndex}
                                </div>
                            );
                        })}
            </div>
        );
    };

    const renderMapPreview = () => {
        if (!areaGeometry) {
            return (
                <div className="report-pattern-stage report-pattern-stage--placeholder">
                    <Empty description="No area geometry available" />
                </div>
            );
        }

        return (
            <div className="report-pattern-stage report-pattern-stage--map">
                <Map
                    ref={mapRef}
                    mapboxAccessToken={MAPBOX_TOKEN}
                    key={`${previewAreaId}-${selectedPatternId}`}
                    initialViewState={{
                        longitude: areaGeometry.coordinates[0][0][0],
                        latitude: areaGeometry.coordinates[0][0][1],
                        zoom: 15,
                    }}
                    style={{ width: '100%', height: '100%' }}
                    mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                    onLoad={() => {
                        if (areaGeometry && mapRef.current) {
                            const coords = areaGeometry.coordinates[0];
                            const lngs = coords.map((c: number[]) => c[0]);
                            const lats = coords.map((c: number[]) => c[1]);
                            const bounds: [[number, number], [number, number]] = [
                                [Math.min(...lngs), Math.min(...lats)],
                                [Math.max(...lngs), Math.max(...lats)],
                            ];
                            mapRef.current.fitBounds(bounds, { padding: 40, duration: 1000 });
                        }
                    }}
                >
                    {/* Area polygon */}
                    <Source
                        id="area"
                        type="geojson"
                        data={{
                            type: 'Feature',
                            geometry: areaGeometry,
                            properties: {},
                        }}
                    >
                        <Layer id="area-fill" type="fill" paint={{ 'fill-color': '#2f54eb', 'fill-opacity': 0.1 }} />
                        <Layer id="area-outline" type="line" paint={{ 'line-color': '#1d39c4', 'line-width': 2 }} />
                    </Source>

                    {/* Area vertices */}
                    {areaGeometry.coordinates[0].map((coord: number[], idx: number) => (
                        <Marker key={`vertex-${idx}`} longitude={coord[0]} latitude={coord[1]} anchor="center">
                            <div
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: '#000',
                                    border: '1px solid white',
                                }}
                            />
                        </Marker>
                    ))}

                    {/* Pattern points */}
                    {patternPoints
                        .filter(p => p.gpsLat != null && p.gpsLon != null)
                        .map(p => {
                            const isMeasured = measuredPointIds.includes(p.id);
                            return (
                                <Marker key={`point-${p.id}`} longitude={p.gpsLon!} latitude={p.gpsLat!} anchor="center">
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onPatternPointClick && selectedPattern) {
                                                onPatternPointClick(p.id, selectedPattern.id, previewAreaId);
                                            }
                                        }}
                                        title={`Click to view reports for Point ${p.pointIndex}`}
                                        style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            background: isMeasured ? '#fa8c16' : '#d9d9d9',
                                            border: '2px solid white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 10,
                                            fontWeight: 'bold',
                                            color: 'white',
                                            opacity: isMeasured ? 1 : 0.5,
                                            cursor: onPatternPointClick ? 'pointer' : 'default',
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (onPatternPointClick) {
                                                e.currentTarget.style.transform = 'scale(1.3)';
                                                e.currentTarget.style.boxShadow = '0 0 8px rgba(250, 140, 22, 0.8)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        {p.pointIndex}
                                    </div>
                                </Marker>
                            );
                        })}
                </Map>
            </div>
        );
    };

    if (patterns.length === 0) {
        return <Empty description="No patterns used in this area during the selected period" />;
    }

    return (
        <div className="report-pattern-workspace">
            <div className="report-pattern-head">
                <div className="report-pattern-title">{section.title || 'Pattern'}</div>
                <div className="report-pattern-meta">{usageWindowLabel}</div>
            </div>

            <div className="report-pattern-body">
                <aside className="report-pattern-rail">
                    <div className="report-pattern-rail-head">
                        <div className="report-pattern-rail-title">Patterns</div>
                        <div className="report-pattern-rail-subtitle">{patterns.length} used in this area</div>
                    </div>

                    <div className="report-pattern-list">
                        {patterns.map((pattern) => (
                            <div
                                key={pattern.id}
                                onClick={() => setSelectedPatternId(pattern.id)}
                                className={`report-pattern-item ${selectedPatternId === pattern.id ? 'report-pattern-item--selected' : ''}`}
                            >
                                <div className="report-pattern-item-top">
                                    <div className="report-pattern-item-title">
                                        {onPatternClick ? (
                                            <PatternLink
                                                patternId={pattern.id}
                                                patternName={pattern.name}
                                                areaId={previewAreaId}
                                                onClick={onPatternClick}
                                                style={{ fontWeight: 700 }}
                                            />
                                        ) : (
                                            <Text strong>{pattern.name}</Text>
                                        )}
                                        <Badge count={pattern.usageCount} style={{ backgroundColor: '#52c41a' }} />
                                    </div>
                                    <Button
                                        size="small"
                                        type="link"
                                        icon={<FileTextOutlined />}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (onPatternClick) {
                                                onPatternClick(pattern.id, previewAreaId);
                                            }
                                        }}
                                        disabled={!onPatternClick}
                                    >
                                        View Report
                                    </Button>
                                </div>

                                <div className="report-pattern-item-tags">
                                    <Tag color="blue">{pattern.patternType || 'custom'}</Tag>
                                    <Tag>{pattern.numPoints} points</Tag>
                                    <Tag color={pattern.coordSystem === 'local' ? 'purple' : 'orange'}>
                                        {pattern.coordSystem === 'local' ? 'Template' : 'GPS'}
                                    </Tag>
                                </div>

                                {pattern.usageHistory && pattern.usageHistory.length > 0 ? (
                                    <div className="report-pattern-item-usage">
                                        <span className="report-pattern-item-usage-label">Recent</span>
                                        {pattern.usageHistory.slice(0, 2).map((usage, idx) => (
                                            <span key={idx} className="report-pattern-item-usage-row">
                                                <span>{dayjs(usage.date).format('MMM DD')}</span>
                                                <span className="report-pattern-item-usage-chip">
                                                    {usage.measurementCount} measurements
                                                </span>
                                            </span>
                                        ))}
                                        {pattern.usageHistory.length > 2 ? (
                                            <span className="report-pattern-item-usage-more">
                                                +{pattern.usageHistory.length - 2} more days
                                            </span>
                                        ) : null}
                                    </div>
                                ) : (
                                    <div className="report-pattern-item-usage report-pattern-item-usage--empty">
                                        No recorded usage in this window
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </aside>

                <div className="report-pattern-main">
                    {selectedPattern ? (
                        <>
                            <div className="report-pattern-main-head">
                                <div>
                                    <div className="report-pattern-main-title">{selectedPattern.name}</div>
                                    <div className="report-pattern-main-meta">
                                        <span>{isTemplateMode ? 'Template preview' : 'Spatial preview'}</span>
                                        {selectedPatternWindowLabel ? <span>{selectedPatternWindowLabel}</span> : null}
                                    </div>
                                </div>
                                <div className="report-pattern-main-tags">
                                    <Tag color="blue">{selectedPattern.patternType || 'custom'}</Tag>
                                    <Tag>{selectedPattern.numPoints} points</Tag>
                                    <Tag color={isTemplateMode ? 'purple' : 'orange'}>
                                        {isTemplateMode ? 'Template' : 'GPS'}
                                    </Tag>
                                </div>
                            </div>

                            <div className="report-pattern-preview-shell">
                                <div className="report-pattern-preview-head">
                                    <div className="report-pattern-preview-title">Pattern preview</div>
                                    <div className="report-pattern-preview-subtitle">
                                        Click any highlighted point to open its reports
                                    </div>
                                </div>
                                {loadingPoints ? (
                                    <div className="report-pattern-stage report-pattern-stage--placeholder">
                                        <Spin tip="Loading pattern points..." />
                                    </div>
                                ) : isTemplateMode ? (
                                    renderTemplatePreview()
                                ) : (
                                    renderMapPreview()
                                )}
                            </div>

                            {selectedPattern.usageHistory && selectedPattern.usageHistory.length > 0 ? (
                                <div className="report-pattern-timeline">
                                    <div className="report-pattern-timeline-head">
                                        <div>
                                            <div className="report-pattern-timeline-title">Usage timeline</div>
                                            {currentUsage ? (
                                                <div className="report-pattern-timeline-subtitle">
                                                    {dayjs(currentUsage.date).format('MMM D, YYYY')} · {currentUsage.measurementCount} measurements
                                                </div>
                                            ) : null}
                                        </div>
                                        {usageHistory.length > 1 ? (
                                            <Button
                                                type={isPlaying ? 'primary' : 'default'}
                                                icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                                onClick={() => setIsPlaying(!isPlaying)}
                                            >
                                                {isPlaying ? 'Pause' : 'Play'}
                                            </Button>
                                        ) : null}
                                    </div>

                                    {selectedPattern.stats ? (
                                        <div className="report-pattern-stats">
                                            <div className="report-pattern-stat">
                                                <span className="report-pattern-stat-label">Total measurements</span>
                                                <span className="report-pattern-stat-value">{selectedPattern.stats.totalMeasurements}</span>
                                            </div>
                                            <div className="report-pattern-stat">
                                                <span className="report-pattern-stat-label">Avg per day</span>
                                                <span className="report-pattern-stat-value">{selectedPattern.stats.avgMeasurementsPerDay.toFixed(1)}</span>
                                            </div>
                                            <div className="report-pattern-stat">
                                                <span className="report-pattern-stat-label">Points measured</span>
                                                <span className="report-pattern-stat-value">
                                                    {measuredPointIds.length} / {selectedPattern.numPoints}
                                                </span>
                                            </div>
                                        </div>
                                    ) : null}

                                    {usageHistory.length > 1 ? (
                                        <div className="report-pattern-slider">
                                            <Slider
                                                min={0}
                                                max={usageHistory.length - 1}
                                                value={selectedDateIndex}
                                                onChange={setSelectedDateIndex}
                                                marks={usageHistory.length <= 10 ? Object.fromEntries(
                                                    usageHistory.map((usage, idx) => [
                                                        idx,
                                                        dayjs(usage.date).format('MMM DD')
                                                    ])
                                                ) : undefined}
                                                tooltip={{
                                                    formatter: (value) => {
                                                        const usage = usageHistory[value || 0];
                                                        return usage ? `${dayjs(usage.date).format('MMM DD, YYYY')} - ${usage.measurementCount} measurements` : '';
                                                    }
                                                }}
                                            />
                                        </div>
                                    ) : null}

                                    {currentUsage ? (
                                        <div className="report-pattern-current-usage">
                                            <div className="report-pattern-current-usage-head">
                                                <Text strong>{dayjs(currentUsage.date).format('MMMM D, YYYY')}</Text>
                                                <span className="report-pattern-current-usage-chip">
                                                    {currentUsage.measurementCount} measurements
                                                </span>
                                            </div>
                                            <Text type="secondary">
                                                Points measured: {measuredPointIds.length} of {selectedPattern.numPoints}
                                                {measuredPointLabels.length > 0 ? (
                                                    <span>
                                                        {' '}({measuredPointLabels.slice(0, 5).join(', ')}
                                                        {measuredPointLabels.length > 5 ? `, +${measuredPointLabels.length - 5} more` : ''})
                                                    </span>
                                                ) : null}
                                            </Text>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <div className="report-pattern-empty">
                            <Empty description="Select a pattern to view details" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReportPatternList;
